import { useSelector } from 'react-redux';
import { ReactNode } from 'react';
import { useLogger } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { getSignUpError } from '@packages/shared/core/user/store/selectors';
import { ApiError } from '@packages/shared/core/common/errors';
import { PasswordStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';

type PasswordQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const PasswordQuizStep = ({ id, renderQuizScreen }: PasswordQuizStepProps) => {
  const logger = useLogger();

  const { back, next, stepData, signUpUser, totalSteps, stepOrder, userPreferences } = useQuizStep(id);

  const errorMessage = useSelector(getSignUpError);

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const { email } = userPreferences;

  const goBack = async () => {
    back();
  };

  const handleSubmit = async (
    password: string,
    {
      setErrors,
    }: {
      setErrors: (errors: Partial<{ password: string }>) => void;
    },
  ) => {
    try {
      await signUpUser({
        email,
        password,
      });
      await next();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({ password: err.originalError.message });
      } else {
        setErrors({ password: 'Something went wrong. Try again later' });
      }
    }
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        signUp: handleSubmit,
        errorMessage,
        totalSteps,
        stepOrder,
        email: userPreferences.email,
        ...(stepData as PasswordStepData),
      })}
    </>
  );
};

export default PasswordQuizStep;
